import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Vote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { EmptyHint, ScopeChips, useScope } from "./Shared";

export function PollsView({ refreshKey }) {
  const { family, members: familyMembers, user } = useAuth();
  const navigate = useNavigate();
  const { scope, setScope, me, scopePersonId } = useScope(family, familyMembers, user?.id);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!family) return;
    try {
      // Scope: "me" / a specific member → only their polls (creator).
      // "family" → everything.
      const params = scopePersonId ? { creator: scopePersonId } : {};
      const { data } = await api.get(`/families/${family.id}/polls`, { params });
      setPolls(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [family, refreshKey, scope]);

  const chips = (
    <ScopeChips scope={scope} onScopeChange={setScope} members={familyMembers || []} me={me} />
  );

  if (loading)
    return (
      <div className="space-y-3" data-testid="polls-view">
        {chips}
        <div className="kinly-card h-32 skeleton" />
      </div>
    );
  if (polls.length === 0)
    return (
      <div className="space-y-3" data-testid="polls-view">
        {chips}
        <EmptyHint
          eyebrow="Polls"
          headline={scope === "me" ? "You haven't started any polls." : "No polls open."}
          body="Start a poll when the family can't agree. Kinly handles the rest."
        />
      </div>
    );

  return (
    <div className="space-y-3" data-testid="polls-view">
      {chips}
      {polls.map((p) => (
        <button
          key={p.id}
          data-testid={`poll-row-${p.id}`}
          onClick={() => navigate(`/poll/${p.id}`)}
          className="w-full kinly-card text-left tap"
        >
          <div className="flex items-center justify-between mb-1.5">
            <Vote className="w-4 h-4 text-primary" />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {p.status}
            </span>
          </div>
          <p className="text-[16px] font-display font-medium">{p.question}</p>
          <p className="text-xs text-muted-foreground mt-2">
            {Object.keys(p.votes || {}).length} / {p.voter_person_ids.length} voted
          </p>
        </button>
      ))}
    </div>
  );
}
